import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { 
  TrendingUp, Users, Target, Phone, Mail, Calendar, 
  Plus, Search, Filter, BarChart3, Clock, CheckCircle2,
  XCircle, AlertCircle, UserPlus, Building, FileText,
  PoundSterling, MapPin, ArrowRight, RefreshCw, Bell, Settings, Trash2, Edit
} from 'lucide-react';
import { toast } from 'sonner';

// Lead statuses
const LEAD_STATUSES = [
  { value: 'new', label: 'New Lead', color: 'bg-blue-100 text-blue-700' },
  { value: 'contacted', label: 'Contacted', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'qualified', label: 'Qualified', color: 'bg-amber-100 text-amber-700' },
  { value: 'proposal_sent', label: 'Proposal Sent', color: 'bg-purple-100 text-purple-700' },
  { value: 'negotiation', label: 'In Negotiation', color: 'bg-orange-100 text-orange-700' },
  { value: 'converted', label: 'Converted', color: 'bg-green-100 text-green-700' },
  { value: 'lost', label: 'Lost', color: 'bg-red-100 text-red-700' }
];

// Lead sources
const LEAD_SOURCES = [
  { value: 'gp_referral', label: 'GP Referral' },
  { value: 'senco_referral', label: 'SENCO Referral' },
  { value: 'community_leader', label: 'Community Leader' },
  { value: 'event', label: 'Promotional Event' },
  { value: 'website', label: 'Website Inquiry' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'word_of_mouth', label: 'Word of Mouth' },
  { value: 'nhs_pathway', label: 'NHS Pathway' },
  { value: 'other', label: 'Other' }
];

// Services offered
const SERVICES = [
  { value: 'neuro_rehab', label: 'Neuro-Rehabilitation' },
  { value: 'paediatric_ot', label: 'Paediatric Occupational Therapy' },
  { value: 'paediatric_physio', label: 'Paediatric Physiotherapy' },
  { value: 'speech_therapy', label: 'Speech & Language Therapy' },
  { value: 'psychology', label: 'Clinical Psychology' },
  { value: 'sensory_integration', label: 'Sensory Integration Therapy' },
  { value: 'early_intervention', label: 'Early Intervention Programme' },
  { value: 'assessment', label: 'Comprehensive Assessment' }
];

export default function CRMDashboardPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  
  // Dialog states
  const [showNewLeadDialog, setShowNewLeadDialog] = useState(false);
  const [showLeadDetailDialog, setShowLeadDetailDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Assignment rules state
  const [assignmentRules, setAssignmentRules] = useState([]);
  const [showRulesDialog, setShowRulesDialog] = useState(false);
  const [showNewRuleDialog, setShowNewRuleDialog] = useState(false);
  const [assignableStaff, setAssignableStaff] = useState([]);
  const [newRule, setNewRule] = useState({
    name: '',
    source: '',
    assign_to_user_id: '',
    auto_create_followup: true,
    followup_days: 7,
    is_active: true
  });
  
  // Follow-up reminders state
  const [followupReminders, setFollowupReminders] = useState({ overdue: [], upcoming: [] });
  
  // New lead form
  const [newLead, setNewLead] = useState({
    name: '',
    email: '',
    phone: '',
    organisation: '',
    source: '',
    services_interested: [],
    notes: '',
    estimated_value: ''
  });

  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';
  const isBusinessDev = user?.role === 'business_development' || isSuperAdmin;

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(sourceFilter !== 'all' && { source: sourceFilter }),
        ...(searchQuery && { search: searchQuery })
      };
      const response = await api.get('/crm/leads', { params });
      setLeads(response.data.leads || []);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      // For now, use mock data if API doesn't exist yet
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter, sourceFilter, searchQuery]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/crm/stats');
      setStats(response.data);
    } catch (error) {
      // Mock stats if API doesn't exist
      setStats({
        total_leads: 0,
        converted_this_month: 0,
        conversion_rate: 0,
        pipeline_value: 0,
        follow_ups_due: 0
      });
    }
  }, [api]);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await api.get('/crm/tasks');
      setTasks(response.data.tasks || []);
    } catch (error) {
      setTasks([]);
    }
  }, [api]);

  const fetchAssignmentRules = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const response = await api.get('/crm/assignment-rules');
      setAssignmentRules(response.data.rules || []);
    } catch (error) {
      console.error('Failed to fetch assignment rules:', error);
    }
  }, [api, isSuperAdmin]);

  const fetchAssignableStaff = useCallback(async () => {
    try {
      const response = await api.get('/crm/assignable-staff');
      setAssignableStaff(response.data.staff || []);
    } catch (error) {
      console.error('Failed to fetch assignable staff:', error);
    }
  }, [api]);

  const fetchFollowupReminders = useCallback(async () => {
    try {
      const response = await api.get('/crm/followup-reminders');
      setFollowupReminders(response.data);
    } catch (error) {
      console.error('Failed to fetch reminders:', error);
    }
  }, [api]);

  useEffect(() => {
    if (isBusinessDev) {
      fetchLeads();
      fetchStats();
      fetchTasks();
      fetchFollowupReminders();
      fetchAssignableStaff();
    }
  }, [isBusinessDev, fetchLeads, fetchStats, fetchTasks, fetchFollowupReminders, fetchAssignableStaff]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchAssignmentRules();
    }
  }, [isSuperAdmin, fetchAssignmentRules]);

  const handleCreateLead = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await api.post('/crm/leads', newLead);
      toast.success('Lead created successfully');
      setShowNewLeadDialog(false);
      setNewLead({
        name: '',
        email: '',
        phone: '',
        organisation: '',
        source: '',
        services_interested: [],
        notes: '',
        estimated_value: ''
      });
      fetchLeads();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (leadId, newStatus) => {
    try {
      await api.patch(`/crm/leads/${leadId}/status`, { status: newStatus });
      toast.success('Lead status updated');
      fetchLeads();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Assignment Rules handlers
  const handleCreateRule = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await api.post('/crm/assignment-rules', newRule);
      toast.success('Assignment rule created');
      setShowNewRuleDialog(false);
      setNewRule({
        name: '',
        source: '',
        assign_to_user_id: '',
        auto_create_followup: true,
        followup_days: 7,
        is_active: true
      });
      fetchAssignmentRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    try {
      await api.delete(`/crm/assignment-rules/${ruleId}`);
      toast.success('Rule deleted');
      fetchAssignmentRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const handleScheduleFollowup = async (leadId, days = 7) => {
    try {
      await api.post(`/crm/leads/${leadId}/schedule-followup?days=${days}`);
      toast.success(`Follow-up scheduled for ${days} days`);
      fetchLeads();
      fetchFollowupReminders();
      fetchTasks();
    } catch (error) {
      toast.error('Failed to schedule follow-up');
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await api.patch(`/crm/tasks/${taskId}/complete`);
      toast.success('Task completed');
      fetchTasks();
      fetchFollowupReminders();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = LEAD_STATUSES.find(s => s.value === status);
    return statusConfig ? (
      <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
    ) : (
      <Badge variant="outline">{status}</Badge>
    );
  };

  if (!isBusinessDev) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700">Access Restricted</h3>
            <p className="text-slate-500 mt-2">CRM Dashboard is available for Business Development and Super Admin only.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f]">CRM Dashboard</h1>
          <p className="text-slate-500 mt-1">Manage leads, referrals, and sales pipeline</p>
        </div>
        <Button onClick={() => setShowNewLeadDialog(true)} className="bg-[#1e3a5f]">
          <Plus className="w-4 h-4 mr-2" />
          New Lead
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1e3a5f]">{stats?.total_leads || 0}</p>
                <p className="text-xs text-slate-500">Total Leads</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1e3a5f]">{stats?.converted_this_month || 0}</p>
                <p className="text-xs text-slate-500">Converted (Month)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Target className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1e3a5f]">{stats?.conversion_rate || 0}%</p>
                <p className="text-xs text-slate-500">Conversion Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <PoundSterling className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1e3a5f]">£{stats?.pipeline_value?.toLocaleString() || 0}</p>
                <p className="text-xs text-slate-500">Pipeline Value</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Bell className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1e3a5f]">{stats?.follow_ups_due || 0}</p>
                <p className="text-xs text-slate-500">Follow-ups Due</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ICRCL Strengths Banner */}
      <Card className="bg-gradient-to-r from-[#1e3a5f] to-[#2a9d8f] text-white">
        <CardContent className="py-4">
          <h3 className="font-semibold mb-2">ICRCL Unique Selling Points</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              <span>100% HCPC-Registered Specialists</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              <span>State-of-the-Art Sensory Equipment</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              <span>Holistic Family-Centred Approach</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              <span>South Asian Community Expertise</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="leads" className="w-full">
        <TabsList className={`grid w-full ${isSuperAdmin ? 'grid-cols-5' : 'grid-cols-4'} max-w-2xl`}>
          <TabsTrigger value="leads">Leads</TabsTrigger>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
          <TabsTrigger value="reminders" className="relative">
            Reminders
            {(followupReminders.overdue_count > 0) && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {followupReminders.overdue_count}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="referrals">Referrals</TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="rules">Auto-Assign</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="leads" className="mt-4">
          {/* Filters */}
          <Card className="mb-4">
            <CardContent className="py-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search leads..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {LEAD_STATUSES.map((status) => (
                      <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="All Sources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    {LEAD_SOURCES.map((source) => (
                      <SelectItem key={source.value} value={source.value}>{source.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Leads Table */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
                </div>
              ) : leads.length === 0 ? (
                <div className="p-8 text-center">
                  <UserPlus className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No leads found. Click "New Lead" to add your first lead.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Organisation</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead>Last Contact</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map((lead) => (
                      <TableRow key={lead.id} className="cursor-pointer hover:bg-slate-50" onClick={() => { setSelectedLead(lead); setShowLeadDetailDialog(true); }}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{lead.name}</p>
                            <p className="text-xs text-slate-500">{lead.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{lead.organisation || '-'}</TableCell>
                        <TableCell>{LEAD_SOURCES.find(s => s.value === lead.source)?.label || lead.source}</TableCell>
                        <TableCell>{getStatusBadge(lead.status)}</TableCell>
                        <TableCell>£{lead.estimated_value?.toLocaleString() || 0}</TableCell>
                        <TableCell>{lead.last_contact ? new Date(lead.last_contact).toLocaleDateString('en-GB') : '-'}</TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); }}>
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tasks" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Upcoming Tasks & Follow-ups
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tasks.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No tasks scheduled</p>
              ) : (
                <div className="space-y-3">
                  {tasks.map((task) => (
                    <div key={task.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${task.priority === 'high' ? 'bg-red-500' : task.priority === 'medium' ? 'bg-amber-500' : 'bg-green-500'}`} />
                        <div>
                          <p className="font-medium">{task.title}</p>
                          <p className="text-sm text-slate-500">{task.lead_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">{new Date(task.due_date).toLocaleDateString('en-GB')}</Badge>
                        <Button size="sm" variant="outline">Complete</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="referrals" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  GP Surgeries
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">0</p>
                <p className="text-xs text-slate-500">Active referral partnerships</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  SENCOs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">0</p>
                <p className="text-xs text-slate-500">School contacts</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Community Leaders
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">0</p>
                <p className="text-xs text-slate-500">Community partnerships</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Reminders Tab */}
        <TabsContent value="reminders" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Overdue Follow-ups */}
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-5 h-5" />
                  Overdue Follow-ups ({followupReminders.overdue_count || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {followupReminders.overdue?.length === 0 ? (
                  <p className="text-center text-slate-500 py-4">No overdue follow-ups</p>
                ) : (
                  <div className="space-y-3">
                    {followupReminders.overdue?.map((lead) => (
                      <div key={lead.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                        <div>
                          <p className="font-medium">{lead.name}</p>
                          <p className="text-xs text-slate-500">{lead.organisation}</p>
                          <p className="text-xs text-red-600 mt-1">
                            Due: {new Date(lead.next_followup).toLocaleDateString('en-GB')}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleScheduleFollowup(lead.id, 1)}>
                            Tomorrow
                          </Button>
                          <Button size="sm" onClick={() => handleScheduleFollowup(lead.id, 3)}>
                            +3 Days
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Upcoming Follow-ups */}
            <Card className="border-amber-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-700">
                  <Clock className="w-5 h-5" />
                  Upcoming Follow-ups ({followupReminders.upcoming_count || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {followupReminders.upcoming?.length === 0 ? (
                  <p className="text-center text-slate-500 py-4">No upcoming follow-ups in the next 3 days</p>
                ) : (
                  <div className="space-y-3">
                    {followupReminders.upcoming?.map((lead) => (
                      <div key={lead.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
                        <div>
                          <p className="font-medium">{lead.name}</p>
                          <p className="text-xs text-slate-500">{lead.organisation}</p>
                          <p className="text-xs text-amber-600 mt-1">
                            Due: {new Date(lead.next_followup).toLocaleDateString('en-GB')}
                          </p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => { setSelectedLead(lead); setShowLeadDetailDialog(true); }}>
                          View Lead
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="referrals" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  GP Surgeries
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">0</p>
                <p className="text-xs text-slate-500">Active referral partnerships</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  SENCOs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">0</p>
                <p className="text-xs text-slate-500">School contacts</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Community Leaders
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">0</p>
                <p className="text-xs text-slate-500">Community partnerships</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Assignment Rules Tab - Super Admin Only */}
        {isSuperAdmin && (
          <TabsContent value="rules" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Auto-Assignment Rules
                  </CardTitle>
                  <CardDescription>
                    Automatically assign new leads to staff based on their source
                  </CardDescription>
                </div>
                <Button onClick={() => { fetchAssignableStaff(); setShowNewRuleDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Rule
                </Button>
              </CardHeader>
              <CardContent>
                {assignmentRules.length === 0 ? (
                  <div className="text-center py-8">
                    <Settings className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">No assignment rules configured</p>
                    <p className="text-sm text-slate-400 mt-1">Create rules to auto-assign leads based on their source</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rule Name</TableHead>
                        <TableHead>Lead Source</TableHead>
                        <TableHead>Assign To</TableHead>
                        <TableHead>Auto Follow-up</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assignmentRules.map((rule) => (
                        <TableRow key={rule.id}>
                          <TableCell className="font-medium">{rule.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {LEAD_SOURCES.find(s => s.value === rule.source)?.label || rule.source}
                            </Badge>
                          </TableCell>
                          <TableCell>{rule.assign_to_name}</TableCell>
                          <TableCell>
                            {rule.auto_create_followup ? (
                              <span className="text-green-600">{rule.followup_days} days</span>
                            ) : (
                              <span className="text-slate-400">Disabled</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={rule.is_active ? 'bg-green-500' : 'bg-slate-400'}>
                              {rule.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-600"
                              onClick={() => handleDeleteRule(rule.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* New Lead Dialog */}
      <Dialog open={showNewLeadDialog} onOpenChange={setShowNewLeadDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Lead</DialogTitle>
            <DialogDescription>
              Add a new potential client to your pipeline
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateLead} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Name *</Label>
                <Input
                  value={newLead.name}
                  onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                  placeholder="Full name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Organisation</Label>
                <Input
                  value={newLead.organisation}
                  onChange={(e) => setNewLead({ ...newLead, organisation: e.target.value })}
                  placeholder="GP Surgery, School, etc."
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newLead.email}
                  onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={newLead.phone}
                  onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                  placeholder="+44 7123 456789"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Lead Source *</Label>
                <Select value={newLead.source} onValueChange={(v) => setNewLead({ ...newLead, source: v })} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select source..." />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_SOURCES.map((source) => (
                      <SelectItem key={source.value} value={source.value}>{source.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estimated Value (£)</Label>
                <Input
                  type="number"
                  value={newLead.estimated_value}
                  onChange={(e) => setNewLead({ ...newLead, estimated_value: e.target.value })}
                  placeholder="0"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Services Interested</Label>
              <div className="grid grid-cols-2 gap-2">
                {SERVICES.map((service) => (
                  <label key={service.value} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={newLead.services_interested.includes(service.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewLead({ ...newLead, services_interested: [...newLead.services_interested, service.value] });
                        } else {
                          setNewLead({ ...newLead, services_interested: newLead.services_interested.filter(s => s !== service.value) });
                        }
                      }}
                      className="rounded"
                    />
                    {service.label}
                  </label>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={newLead.notes}
                onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                placeholder="Additional information about this lead..."
                rows={3}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNewLeadDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={saving || !newLead.name || !newLead.source}>
                {saving ? 'Creating...' : 'Create Lead'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog open={showLeadDetailDialog} onOpenChange={setShowLeadDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedLead?.name}</DialogTitle>
            <DialogDescription>{selectedLead?.organisation}</DialogDescription>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <p className="font-medium">{selectedLead.email || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Phone</Label>
                  <p className="font-medium">{selectedLead.phone || '-'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Source</Label>
                  <p className="font-medium">{LEAD_SOURCES.find(s => s.value === selectedLead.source)?.label || selectedLead.source}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Estimated Value</Label>
                  <p className="font-medium">£{selectedLead.estimated_value?.toLocaleString() || 0}</p>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Status</Label>
                <Select value={selectedLead.status} onValueChange={(v) => handleUpdateStatus(selectedLead.id, v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUSES.map((status) => (
                      <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedLead.notes && (
                <div>
                  <Label className="text-xs text-slate-500">Notes</Label>
                  <p className="text-sm">{selectedLead.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeadDetailDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Assignment Rule Dialog */}
      <Dialog open={showNewRuleDialog} onOpenChange={setShowNewRuleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Auto-Assignment Rule</DialogTitle>
            <DialogDescription>
              Automatically assign new leads to staff based on their source
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateRule} className="space-y-4">
            <div className="space-y-2">
              <Label>Rule Name *</Label>
              <Input
                value={newRule.name}
                onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                placeholder="e.g., GP Referrals to Sales Team"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Lead Source *</Label>
              <Select value={newRule.source} onValueChange={(v) => setNewRule({ ...newRule, source: v })} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select lead source..." />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_SOURCES.map((source) => (
                    <SelectItem key={source.value} value={source.value}>{source.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Assign To *</Label>
              <Select value={newRule.assign_to_user_id} onValueChange={(v) => setNewRule({ ...newRule, assign_to_user_id: v })} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select staff member..." />
                </SelectTrigger>
                <SelectContent>
                  {assignableStaff.map((staff) => (
                    <SelectItem key={staff.id} value={staff.id}>
                      {staff.first_name} {staff.last_name} ({staff.role?.replace(/_/g, ' ')})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium">Auto-create Follow-up Task</p>
                <p className="text-sm text-slate-500">Automatically schedule a follow-up when lead is created</p>
              </div>
              <Switch
                checked={newRule.auto_create_followup}
                onCheckedChange={(checked) => setNewRule({ ...newRule, auto_create_followup: checked })}
              />
            </div>
            
            {newRule.auto_create_followup && (
              <div className="space-y-2">
                <Label>Follow-up in (days)</Label>
                <Input
                  type="number"
                  min="1"
                  max="30"
                  value={newRule.followup_days}
                  onChange={(e) => setNewRule({ ...newRule, followup_days: parseInt(e.target.value) || 7 })}
                />
              </div>
            )}
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNewRuleDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={saving || !newRule.name || !newRule.source || !newRule.assign_to_user_id}>
                {saving ? 'Creating...' : 'Create Rule'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
